import { z } from 'zod';

/**
 * Operator-curated replacement paths for marketplace installers and hosted
 * builder/SEO products. A blueprint is intentionally richer than a Coolify
 * template: it states who maintains the workload, where versions come from,
 * what persists, and what backup/migration work remains.
 */

export const SERVICE_BLUEPRINT_KINDS = [
  'service-template',
  'wordpress-operations',
  'application-starter',
  'external-integration',
] as const;
export type ServiceBlueprintKind = (typeof SERVICE_BLUEPRINT_KINDS)[number];

export const SERVICE_BLUEPRINT_BACKUP_STATUSES = [
  'covered',
  'partial',
  'external',
  'not-applicable',
] as const;
export type ServiceBlueprintBackupStatus =
  (typeof SERVICE_BLUEPRINT_BACKUP_STATUSES)[number];

export interface ServiceBlueprintResourceRequirement {
  resource: 'application' | 'database' | 'service' | 'persistent-volume' | 'external-account';
  count: number;
  detail: string;
}

export interface ServiceBlueprintDto {
  id: string;
  name: string;
  summary: string;
  kind: ServiceBlueprintKind;
  /** Coolify template identity for a provisionable one-click service. */
  templateSlug: string | null;
  /** True only when the connected panel can take the blueprint's action now. */
  available: boolean;
  unavailableReason: string | null;
  versionSource: string;
  requiredResources: ServiceBlueprintResourceRequirement[];
  persistentData: string[];
  backupCoverage: {
    status: ServiceBlueprintBackupStatus;
    detail: string;
  };
  maintenanceOwner: string;
  /**
   * Secret names only. Values always enter through the owning module's normal
   * shown-once/encrypted environment or SettingsStore path.
   */
  requiredSecrets: string[];
  migrationNotes: string[];
  exportImportChecklist: string[];
  action:
    | { type: 'create-service' }
    | { type: 'open-wordpress'; href: string }
    | { type: 'open-application'; hrefTemplate: string }
    | { type: 'external-checklist' };
}

export interface ServiceBlueprintCatalogDto {
  blueprints: ServiceBlueprintDto[];
  /** Advanced raw Coolify templates remain separate from replacement claims. */
  note: string;
}

/**
 * Blueprint creation is a source variant of the normal service create route.
 * Only `service-template` blueprints are accepted by that route; the other
 * kinds deliberately hand off to their owning application/module/checklist.
 */
export const createServiceFromBlueprintSchema = z.object({
  source: z.literal('blueprint'),
  blueprintId: z.string().trim().min(1).max(100),
});
